import React, { useState, useEffect } from 'react';
import { fetchPlayerTeamHistory } from '../services/AccountService';
import { PlayerTeamHistory } from '../models/PlayerTeamHistory';
import { fetchTeamByTeamIdAndSeason } from '../services/TeamService';
import { Team } from '../models/Team';
import { RATING_KIND_VERSION, RatingKind, pickRating } from '../utils/ratingDisplay';

interface CompactTeamHistory {
    season: number;
    teamId: string;
    teamName: string;
    teamLogo: string;
    division?: string;
    rating: number;
    /** Which rating `rating` actually came from: 3.0 where the season has it, else 2.1, else 2.0. */
    ratingKind?: RatingKind;
    startTime: Date | string;
  }
  
  interface PlayerTeamHistoryComponentProps {
    steamId: string;
    isDark: boolean;
    compact?: boolean;
    /** "flat": the broadcast-flat surfaces of the newer pages (predictions). Default: the orange-glow card. */
    appearance?: 'glow' | 'flat';
  }

  // Broadcast-flat look (MatchPage2 / predictions): grey panel, #1c1c1c header strip, muted caps labels.
  const FLAT = {
    container: { background: '#232323', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    headRow: { background: '#1c1c1c', borderBottom: '1px solid #333', height: '30px' },
    th: {
      color: 'rgba(255, 255, 255, 0.5)',
      fontWeight: 700,
      padding: '7px 6px',
      fontSize: '0.66rem',
      letterSpacing: '0.5px',
      textTransform: 'uppercase' as const,
      background: 'transparent',
    },
    rowBg: (index: number) => (index % 2 === 1 ? 'rgba(255, 255, 255, 0.015)' : 'transparent'),
    rowHover: 'rgba(255, 255, 255, 0.04)',
    rowBorder: '1px solid #262626',
    season: 'rgba(255, 255, 255, 0.55)',
    text: '#e2e8f0',
  };
  
  const PlayerTeamHistoryComponent: React.FC<PlayerTeamHistoryComponentProps> = ({ 
    steamId, 
    isDark,
    compact = false,
    appearance = 'glow'
  }) => {
    const flat = appearance === 'flat';
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState<CompactTeamHistory[]>([]);
    const [error, setError] = useState<string | null>(null);
  
    useEffect(() => {
      const fetchHistory = async () => {
        try {
          setLoading(true);
          
          // Fetch player's team history
          const historyData = await fetchPlayerTeamHistory(steamId);
          
          // Get team details for each history entry
          const historyWithTeams = await Promise.all(
            historyData.map(async (entry: PlayerTeamHistory) => {
              let team: Team | null = null;
              try {
                team = await fetchTeamByTeamIdAndSeason(entry.teamId, entry.season);
              } catch (err) {
                console.error(`Failed to fetch team for ${entry.teamId} in season ${entry.season}`, err);
              }
              
              const picked = entry.rating === -1
                ? null
                : pickRating({ rating30: entry.rating30, rating21: entry.rating21, rating2: entry.rating });

              return {
                season: entry.season,
                teamId: entry.teamId,
                teamName: team?.teamName || 'Unknown Team',
                teamLogo: team?.teamLogo || '',
                division: entry.division ?? team?.division,
                // 3.0 -> 2.1 -> 2.0. `rating` is the model's 2.0 field, -1 its "none" sentinel.
                rating: picked?.value ?? 0,
                ratingKind: picked?.kind,
                startTime: entry.startTime,
              };
            })
          );
          
          // Sort by startTime (descending) to handle mid-season team switches correctly
          historyWithTeams.sort((a, b) => {
            const dateA = new Date(a.startTime).getTime();
            const dateB = new Date(b.startTime).getTime();
            return dateB - dateA; // Most recent first
          });
          setHistory(historyWithTeams);
          setError(null);
        } catch (err) {
          console.error('Error fetching player team history:', err);
          setError('Failed to load team history');
        } finally {
          setLoading(false);
        }
      };
  
      fetchHistory();
    }, [steamId]);
  
    // Convert division to a simple letter/number
    const getSimpleDivision = (division?: string): string => {
      if (!division) return '-';
      
      if (division === 'ligaen') return 'L';

      // Liga Kvalifikation N tournaments → "K1", "K2", …
      if (division.startsWith('liga-kval')) {
        const n = division.split('-')[2];
        return n ? `K${n}` : 'K';
      }

      // For divisions like 1div, 2divA, 3divB, etc.
      if (division.startsWith('1div')) return '1';
      if (division.startsWith('2div')) {
        // Check if there's a letter at the end (like A or B)
        const letter = division.charAt(4);
        return letter && letter.match(/[A-Z]/i) ? `2${letter}` : '2';
      }
      if (division.startsWith('3div')) {
        const letter = division.charAt(4);
        return letter && letter.match(/[A-Z]/i) ? `3${letter}` : '3';
      }
      if (division.startsWith('4div')) {
        const letter = division.charAt(4);
        return letter && letter.match(/[A-Z]/i) ? `4${letter}` : '4';
      }
      if (division.startsWith('5div')) {
        const letter = division.charAt(4);
        return letter && letter.match(/[A-Z]/i) ? `5${letter}` : '5';
      }
      
      // Fallback for any other format
      return division.substring(0, 2);
    };
  
    if (loading) {
      return <div className="text-center"><small>Loading...</small></div>;
    }
  
    if (error) {
      return <div className="text-center text-danger"><small>Error!</small></div>;
    }
  
    if (history.length === 0) {
      return <div className="text-center"><small>No history</small></div>;
    }
  
    return (
      <div 
        className={`player-history-compact ${isDark ? 'text-white' : ''}`} 
        style={{ 
          fontSize: compact ? '0.8rem' : '0.875rem',
          ...(flat && { color: FLAT.text, ...FLAT.container }),
          ...(isDark && !flat && {
            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(37, 37, 37, 0.95) 100%)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 102, 0, 0.1)',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden'
          })
        }}
      >
        <table 
          className="mb-0"
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'transparent'
          }}
        >
          <thead>
            <tr style={flat ? FLAT.headRow : isDark ? {
              borderBottom: '2px solid rgba(255, 102, 0, 0.3)',
              height: '32px'
            } : {}}>
              <th style={flat ? FLAT.th : isDark ? {
                color: '#ff9933',
                fontWeight: '600',
                padding: '8px 6px',
                fontSize: compact ? '0.75rem' : '0.8rem',
                background: 'transparent'
              } : { padding: '8px 6px' }}>S</th>
              <th style={flat ? FLAT.th : isDark ? {
                color: '#ff9933',
                fontWeight: '600',
                padding: '8px 6px',
                fontSize: compact ? '0.75rem' : '0.8rem',
                background: 'transparent'
              } : { padding: '8px 6px' }}>Team</th>
              <th style={flat ? FLAT.th : isDark ? {
                color: '#ff9933',
                fontWeight: '600',
                padding: '8px 6px',
                fontSize: compact ? '0.75rem' : '0.8rem',
                background: 'transparent'
              } : { padding: '8px 6px' }}>Div</th>
              <th style={flat ? FLAT.th : isDark ? {
                color: '#ff9933',
                fontWeight: '600',
                padding: '8px 6px',
                fontSize: compact ? '0.75rem' : '0.8rem',
                background: 'transparent'
              } : { padding: '8px 6px' }}>Rat</th>
            </tr>
          </thead>
          <tbody>
            {history.map((entry, index) => (
              <tr
                key={`${entry.teamId}-${entry.season}-${entry.division ?? ''}`}
                style={flat ? {
                  backgroundColor: FLAT.rowBg(index),
                  color: FLAT.text,
                  borderBottom: FLAT.rowBorder,
                  height: '30px',
                  transition: 'background-color 0.2s ease'
                } : {
                  backgroundColor: isDark
                    ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                    : index % 2 === 0 ? '#f8f9fa' : 'transparent',
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                  borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid #dee2e6',
                  height: '32px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (flat) {
                    e.currentTarget.style.backgroundColor = FLAT.rowHover;
                  } else if (isDark) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 102, 0, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (flat) {
                    e.currentTarget.style.backgroundColor = FLAT.rowBg(index);
                  } else if (isDark) {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent';
                  }
                }}
              >
                <td style={{ 
                  padding: '6px',
                  color: flat ? FLAT.season : isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                  fontVariantNumeric: flat ? 'tabular-nums' : undefined
                }}>{entry.season}</td>
                <td style={{ padding: '6px' }}>
                  <img 
                    src={entry.teamLogo} 
                    alt={entry.teamName} 
                    title={entry.teamName}
                    style={flat ? { height: '22px', width: '22px', objectFit: 'contain', display: 'block' } : { height: '28px', width: '28px', objectFit: 'contain' }}
                  />
                </td>
                <td style={{ 
                  padding: '6px',
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000'
                }}>
                  {getSimpleDivision(entry.division)}
                </td>
                <td style={{ 
                  padding: '6px',
                  color: flat ? '#fff' : isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                  fontWeight: flat ? 600 : undefined,
                  fontVariantNumeric: flat ? 'tabular-nums' : undefined
                }}>{entry.rating > 0 ? (
                  <span title={entry.ratingKind && entry.ratingKind !== 'r3'
                    ? `Rating ${RATING_KIND_VERSION[entry.ratingKind]} — Rating 3.0 er ikke tilgængelig for denne sæson`
                    : undefined}>
                    {entry.rating.toFixed(2)}
                    {entry.ratingKind && entry.ratingKind !== 'r3' && (
                      <span style={{ marginLeft: 2, fontSize: '0.75em', verticalAlign: 'super' }}>*</span>
                    )}
                  </span>
                ) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  export default PlayerTeamHistoryComponent;